import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { InboundComposeWizard } from '../components/InboundComposeWizard';
import {
  useComposeInboundCampaign,
  useInboundCampaignClone,
} from '../api';
import { buildInboundComposeRequest, hydrateInboundFromClone } from '../payload';
import {
  DEFAULT_INBOUND_FORM_VALUES,
  type InboundComposeFormValues,
} from '../types';

export function CloneInboundCampaignPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const { campaignId } = useParams<{ campaignId: string }>();
  const id = Number(campaignId);

  const clone = useInboundCampaignClone(id);
  const compose = useComposeInboundCampaign();

  const initial = useMemo<InboundComposeFormValues | null>(() => {
    if (!clone.data) return null;
    const base = hydrateInboundFromClone(clone.data);
    return {
      ...DEFAULT_INBOUND_FORM_VALUES,
      ...base,
      // Force a fresh name + no carried-over CLI selection — the original DID
      // may still be running.
      campaignName: base.campaignName ? `${base.campaignName} (copy)` : '',
      selectedCLI: [],
    };
  }, [clone.data]);

  if (clone.isLoading || !initial) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading campaign…
      </div>
    );
  }

  return (
    <InboundComposeWizard
      mode="clone"
      initialValues={initial}
      isPending={compose.isPending}
      onSubmit={(values) => {
        compose.mutate(
          buildInboundComposeRequest({ userId: Number(userId), values }),
          {
            onSuccess: (res) => {
              if (res?.message === 'Parameters Incorrect') {
                toast.error(res.message);
                return;
              }
              toast.success(res?.message ?? 'Inbound campaign cloned');
              navigate('/inbound-campaign-list');
            },
            onError: () => toast.error('Could not clone the inbound campaign.'),
          },
        );
      }}
    />
  );
}
